import { useState } from 'react'
import { Link } from 'react-router-dom'
import { FotoPerfilCard } from '../components/FotoPerfilCard'
import { HubHeader } from '../components/HubHeader'
import { HubPageSkeleton } from '../components/HubPageSkeleton'
import { useHubContext } from '../components/HubLayout'
import { MissoesList } from '../components/MissoesList'
import { PwaOptinCard } from '../components/PwaOptinCard'
import { ShareButtons } from '../components/ShareButtons'
import { StartUpsellCard } from '../components/StartUpsellCard'
import { StartWizardCard } from '../components/StartWizardCard'
import { WhatsAppIcon } from '../components/WhatsAppIcon'
import { apiConcluirMissao } from '../lib/api'

export function HubPage() {
  const { hub, setHub, setError, error } = useHubContext()
  const [copyOk, setCopyOk] = useState(false)
  const [busyMissao, setBusyMissao] = useState<number | null>(null)
  const [missaoError, setMissaoError] = useState('')

  if (!hub) {
    return null
  }

  if (!hub.inicio_loaded) {
    return (
      <>
        <HubHeader
          eyebrow="Olá,"
          title={hub.user.nome}
          subtitle={hub.campanha.nome || undefined}
          showLogo
          hideBack
        />
        {error ? <p className="bp-error">{error}</p> : null}
        <HubPageSkeleton />
      </>
    )
  }

  async function copyLink(link: string) {
    try {
      await navigator.clipboard.writeText(link)
      setCopyOk(true)
      setTimeout(() => setCopyOk(false), 2000)
    } catch {
      setError('Não foi possível copiar o link.')
    }
  }

  const convite = hub.convite
  const isStart = hub.plano?.codigo === 'start'
  const hasRanking = !isStart && !!hub.top_ranking_dia?.length
  const hasMissoes = !isStart && !!hub.missoes?.length
  const hasMoldura = !!hub.moldura?.arquivo
  const hasColinha = !!(hub.colinha && (hub.colinha.arquivo || hub.colinha.link))
  const medalha = hub.medalha ?? { estrelas: 0, diretos: 0, indiretos: 0, total: 0 }
  const election = hub.election ?? { estado: '', label: '', data: '' }
  const campanhaNome = hub.campanha.nome?.trim() || 'Campanha'
  const showGestaoRede = hub.bottom_nav.some((item) => item.key === 'rede')

  return (
    <>
      <HubHeader
        eyebrow="Olá,"
        title={hub.user.nome}
        subtitle={hub.campanha.nome ? `${hub.campanha.nome}${isStart ? ' · Start' : ''}` : undefined}
        showLogo
        hideBack
      />

      <StartWizardCard hub={hub} onDone={setHub} onError={setError} />

      {/* 1 — App e Avisos */}
      <PwaOptinCard />

      <StartUpsellCard hub={hub} />

      <div className="bp-hub-grid">
        {/* 2 — Convidar para a rede */}
        {convite ? (
          <section className="bp-card bp-hub-grid__full" id="hubConvite">
            <p className="bp-card__eyebrow">Convide para a rede</p>
            <p className="bp-muted">Copie o link, mostre o QR ou envie pelo WhatsApp.</p>
            <input className="bp-input" readOnly value={convite.convite} aria-label="Link de convite" />
            <div className="bp-hub-actions bp-hub-actions--3" style={{ marginTop: '0.75rem' }}>
              <a className="bp-btn bp-btn--wa" href={convite.whatsapp} rel="noopener noreferrer">
                <WhatsAppIcon />
                WhatsApp
              </a>
              <Link className="bp-btn bp-btn--soft" to="/hub/convidar">
                <span className="bp-btn__icon" aria-hidden>
                  ▦
                </span>
                QR / Mais
              </Link>
              <button type="button" className="bp-btn bp-btn--primary" onClick={() => copyLink(convite.convite)}>
                <span className="bp-btn__icon" aria-hidden>
                  📋
                </span>
                {copyOk ? 'Copiado!' : 'Copiar'}
              </button>
            </div>
          </section>
        ) : null}

        {/* 3 — Foto de perfil */}
        <FotoPerfilCard className="bp-hub-grid__full" />

        {/* 4 — Eleição */}
        {!isStart ? (
          <aside className="bp-card bp-card--compact bp-hub-kpi bp-hub-grid__full" aria-label="Countdown da eleição">
            <p className="bp-hub-kpi__label">Eleição · {election.data}</p>
            <p className="bp-hub-kpi__value bp-hub-kpi__value--sm">{election.label}</p>
            <p className="bp-hub-kpi__meta">Foco em convidar e mobilizar a rede.</p>
          </aside>
        ) : null}

        {/* 5 — Gestão da Rede | 6 — Nome Campanha */}
        {showGestaoRede ? (
          <section className="bp-card bp-card--compact" aria-label="Gestão da Rede">
            <p className="bp-hub-kpi__label">Rede</p>
            <p className="bp-hub-kpi__meta" style={{ marginBottom: '0.75rem' }}>
              Convidados, árvore, medalhas e ranking.
            </p>
            <Link className="bp-btn bp-btn--primary" to="/hub/rede">
              <span className="bp-btn__icon" aria-hidden>
                ⑂
              </span>
              Gestão da Rede
            </Link>
          </section>
        ) : null}

        <section
          className={`bp-card bp-card--compact${!showGestaoRede ? ' bp-hub-grid__full' : ''}`}
          aria-label={campanhaNome}
        >
          <p className="bp-hub-kpi__label">Campanha</p>
          <p className="bp-hub-kpi__meta" style={{ marginBottom: '0.75rem' }}>
            Biografia, avisos, agenda e materiais.
          </p>
          <Link className="bp-btn bp-btn--soft" to="/hub/campanha">
            <span className="bp-btn__icon" aria-hidden>
              📢
            </span>
            {campanhaNome}
          </Link>
        </section>

        {/* 7 — Sua medalha / Sua rede (Start) */}
        {!isStart ? (
          <section className="bp-card bp-card--compact bp-hub-kpi bp-hub-grid__full" aria-label="Sua medalha">
            <p className="bp-hub-kpi__label" style={{ color: 'var(--bp-accent-amber)' }}>
              Sua medalha
            </p>
            <div className="bp-hub-kpi__row">
              <p className="bp-hub-kpi__value" aria-label={`${medalha.estrelas} estrelas`}>
                {medalha.estrelas > 0 ? '★'.repeat(medalha.estrelas) : '—'}
              </p>
              <p className="bp-hub-kpi__meta" style={{ textAlign: 'right', margin: 0 }}>
                <strong style={{ color: 'var(--bp-text)', fontSize: '1.1rem' }}>{medalha.diretos}</strong>
                <span style={{ display: 'block' }}>diretos</span>
              </p>
            </div>
            <p className="bp-hub-kpi__meta">
              {medalha.indiretos} indiretos · {medalha.total} no total
            </p>
            <Link className="bp-btn bp-btn--amber" to="/hub/medalhas">
              <span className="bp-btn__icon" aria-hidden>
                ★
              </span>
              Medalhas
            </Link>
          </section>
        ) : (
          <section className="bp-card bp-card--compact bp-hub-kpi bp-hub-grid__full" aria-label="Sua rede">
            <p className="bp-hub-kpi__label" style={{ color: 'var(--bp-accent-amber)' }}>
              Sua rede
            </p>
            <div className="bp-hub-kpi__row">
              <div>
                <p className="bp-hub-kpi__value">{medalha.diretos}</p>
                <p className="bp-hub-kpi__meta">diretos</p>
              </div>
              <div style={{ textAlign: 'right' }}>
                <p className="bp-hub-kpi__value bp-hub-kpi__value--sm">{medalha.total}</p>
                <p className="bp-hub-kpi__meta">
                  no total
                  {hub.plano?.teto_cadastros
                    ? ` · teto ${hub.plano.teto_cadastros.toLocaleString('pt-BR')}`
                    : ''}
                </p>
              </div>
            </div>
            <Link className="bp-btn bp-btn--primary" to="/hub/convidados">
              <span className="bp-btn__icon" aria-hidden>
                👥
              </span>
              Ver convidados
            </Link>
          </section>
        )}

        {/* 8 — Minha colinha */}
        {hasColinha ? (
          <section className="bp-card bp-hub-grid__full">
            <p className="bp-card__eyebrow">Minha Colinha</p>
            <p className="bp-muted">Baixe a imagem ou envie no WhatsApp.</p>
            {hub.colinha?.arquivo ? (
              <div className="bp-preview">
                <img src={hub.colinha.url || ''} alt="Colinha" />
              </div>
            ) : null}
            <div className="bp-stack" style={{ marginTop: '0.75rem' }}>
              <Link className="bp-btn bp-btn--primary" to="/hub/colinha">
                <span className="bp-btn__icon" aria-hidden>
                  🗳
                </span>
                Abrir colinha
              </Link>
            </div>
            <ShareButtons
              url={hub.urls.colinha_share || ''}
              title={hub.campanha.nome ? `Minha colinha — ${hub.campanha.nome}` : 'Minha colinha'}
              tipo="colinha"
              id={hub.campanha.id}
            />
          </section>
        ) : null}

        {hasRanking ? (
          <section className="bp-card bp-hub-grid__full">
            <div className="bp-hub-card-head">
              <strong>Top 3 de hoje</strong>
              <Link to="/hub/ranking?periodo=dia">Ver ranking</Link>
            </div>
            <ol className="bp-hub-rank">
              {(hub.top_ranking_dia ?? []).map((row, i) => (
                <li key={i} className="bp-hub-rank__item">
                  <span className="bp-hub-rank__pos" aria-hidden>
                    {i + 1}
                  </span>
                  <span className="bp-hub-rank__name">{row.nome}</span>
                  <span className="bp-hub-rank__pts">
                    {Number(row.pontuacao_total || 0).toLocaleString('pt-BR')} pts
                  </span>
                </li>
              ))}
            </ol>
          </section>
        ) : null}

        {hasMoldura ? (
          <section className="bp-card bp-hub-grid__full">
            <p className="bp-card__eyebrow">Eu apoio</p>
            <p className="bp-muted">Coloque sua foto na moldura, baixe ou envie no WhatsApp.</p>
            <div className="bp-preview">
              <img src={hub.moldura?.url || ''} alt="Moldura" />
            </div>
            <div className="bp-stack" style={{ marginTop: '0.75rem' }}>
              <Link className="bp-btn bp-btn--accent" to="/hub/eu-apoio">
                <span className="bp-btn__icon" aria-hidden>
                  🖼
                </span>
                Montar foto
              </Link>
            </div>
            <ShareButtons
              url={hub.urls.eu_apoio_share || ''}
              title={hub.campanha.nome ? `Eu apoio ${hub.campanha.nome}` : 'Eu apoio'}
              tipo="moldura"
              id={hub.campanha.id}
            />
          </section>
        ) : null}

        {hasMissoes ? (
          <section className="bp-card bp-hub-grid__full">
            <p className="bp-card__eyebrow">Missões de hoje</p>
            <MissoesList
              items={hub.missoes ?? []}
              compact
              busyId={busyMissao}
              onConcluir={async (id) => {
                setBusyMissao(id)
                setMissaoError('')
                try {
                  const payload = await apiConcluirMissao(id)
                  setHub({ ...hub, missoes: payload.missoes.slice(0, 5) })
                } catch (err) {
                  setMissaoError(err instanceof Error ? err.message : 'Não foi possível concluir.')
                } finally {
                  setBusyMissao(null)
                }
              }}
            />
            {missaoError ? <p className="bp-error">{missaoError}</p> : null}
            <Link className="bp-btn bp-btn--soft" to="/hub/missoes" style={{ marginTop: '0.75rem' }}>
              <span className="bp-btn__icon" aria-hidden>
                ✓
              </span>
              Ver todas
            </Link>
          </section>
        ) : null}
      </div>
    </>
  )
}
